import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { hasPermission, type Permission } from '@aramis/shared';
import { api, clearTokens, ensureSession, onSessionExpired, setTokens } from './api';

export interface Me {
  id: string; name: string; email: string | null; phone: string | null; mustChangePassword: boolean;
  role: { key: string; name: string }; permissions: string[];
}
interface AuthState {
  me: Me | null;
  status: 'loading' | 'signed-in' | 'signed-out' | 'setup';
  can: (p: Permission) => boolean;
  login: (login: string, password: string) => Promise<void>;
  setup: (body: { setupCode: string; name: string; phone: string; email?: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
}
const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  const reload = useCallback(async () => {
    try {
      if (await ensureSession()) {
        setMe(await api<Me>('/admin/auth/me'));
        setStatus('signed-in');
        return;
      }
    } catch { /* fall through */ }
    const { required } = await api<{ required: boolean }>('/admin/setup').catch(() => ({ required: false }));
    setMe(null);
    setStatus(required ? 'setup' : 'signed-out');
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => onSessionExpired(() => { clearTokens(); setMe(null); setStatus('signed-out'); }), []);

  const value = useMemo<AuthState>(() => ({
    me, status,
    can: (p) => !!me && hasPermission(me.permissions, p),
    login: async (login, password) => {
      setTokens(await api('/admin/auth/login', { body: { login, password } }));
      await reload();
    },
    setup: async (body) => {
      setTokens(await api('/admin/setup', { body }));
      await reload();
    },
    logout: async () => {
      try { await api('/admin/auth/logout', { body: { refreshToken: localStorage.getItem('aramis-admin-rt') ?? '' } }); } catch { /* ignore */ }
      clearTokens();
      setMe(null);
      setStatus('signed-out');
    },
    reload,
  }), [me, status, reload]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

// ------------------------------------------------------------------ theme
export type ThemeChoice = 'system' | 'light' | 'dark';
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(() => {
    try { return (localStorage.getItem('aramis-admin-theme') as ThemeChoice) || 'system'; } catch { return 'system'; }
  });
  useEffect(() => {
    const root = document.documentElement;
    if (choice === 'system') delete root.dataset.theme; else root.dataset.theme = choice;
    try { localStorage.setItem('aramis-admin-theme', choice); } catch { /* ignore */ }
  }, [choice]);
  return [choice, setChoice] as const;
}
